import React, { useMemo, useState } from 'react';

// 4-bucket performance pie chart:
//   Excellent (top 25%), Good (25-50%), Needs Improvement (50-75%), Critical (bottom 25%)
// Input: `scores` = { [district]: numericScore, higherIsBetter?: bool }
// Clicking a slice opens a small district-list panel.
//
// Score = number of metrics where district ranks in the top third across all
// districts, minus number where it ranks in the bottom third. Higher = better.
// Caller computes it; we just bucket by percentile.
export default function PerformancePieChart({ title = 'District Performance Overview', scores = {}, onSelectDistrict }) {
  const [activeBucket, setActiveBucket] = useState(null);

  const buckets = useMemo(() => {
    const entries = Object.entries(scores).filter(([, s]) => Number.isFinite(s));
    entries.sort((a, b) => b[1] - a[1]); // higher score = better
    const n = entries.length;
    if (n === 0) return null;
    const q = Math.max(1, Math.round(n / 4));
    return {
      excellent: entries.slice(0, q).map(([d]) => d),
      good:      entries.slice(q, q * 2).map(([d]) => d),
      needsImp:  entries.slice(q * 2, q * 3).map(([d]) => d),
      critical:  entries.slice(q * 3).map(([d]) => d),
      total: n,
    };
  }, [scores]);

  if (!buckets) {
    return (
      <div className="bg-white border border-slate-200 p-4 text-center text-xs text-slate-500">
        {title} — not enough district data yet.
      </div>
    );
  }

  const slices = [
    { key: 'excellent', label: 'Excellent',        count: buckets.excellent.length, color: '#10b981', textOn: '#065f46' },
    { key: 'good',      label: 'Good',             count: buckets.good.length,      color: '#3b82f6', textOn: '#1e40af' },
    { key: 'needsImp',  label: 'Needs Improvement',count: buckets.needsImp.length,  color: '#f59e0b', textOn: '#92400e' },
    { key: 'critical',  label: 'Critical',         count: buckets.critical.length,  color: '#ef4444', textOn: '#991b1b' },
  ];

  // Build SVG donut segments
  const R = 90;    // outer radius
  const IR = 55;   // inner radius (donut hole)
  const CX = 110, CY = 110;
  let cum = 0;
  const segments = slices.map(s => {
    const frac = buckets.total === 0 ? 0 : s.count / buckets.total;
    const startAngle = cum * Math.PI * 2 - Math.PI / 2;
    cum += frac;
    const endAngle = cum * Math.PI * 2 - Math.PI / 2;
    const large = frac > 0.5 ? 1 : 0;
    const x1 = CX + R * Math.cos(startAngle), y1 = CY + R * Math.sin(startAngle);
    const x2 = CX + R * Math.cos(endAngle),   y2 = CY + R * Math.sin(endAngle);
    const xi1 = CX + IR * Math.cos(endAngle), yi1 = CY + IR * Math.sin(endAngle);
    const xi2 = CX + IR * Math.cos(startAngle), yi2 = CY + IR * Math.sin(startAngle);
    const d = frac === 0 ? '' :
      `M ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${xi1} ${yi1} A ${IR} ${IR} 0 ${large} 0 ${xi2} ${yi2} Z`;
    // Label midpoint (only for non-tiny slices)
    const mid = startAngle + (endAngle - startAngle) / 2;
    const labelR = (R + IR) / 2;
    const lx = CX + labelR * Math.cos(mid);
    const ly = CY + labelR * Math.sin(mid);
    return { ...s, d, frac, lx, ly };
  });

  const activeList = activeBucket ? buckets[activeBucket] : null;
  const activeSlice = activeBucket ? slices.find(s => s.key === activeBucket) : null;

  return (
    <div className="bg-white border border-slate-200 shadow-sm">
      <div className="px-4 py-3 bg-[#0f2e4d] text-white text-sm font-semibold uppercase tracking-wide">
        {title}
      </div>
      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Donut */}
        <div className="flex justify-center">
          <svg width="220" height="220" viewBox="0 0 220 220">
            {segments.map(s => (
              <g key={s.key}>
                <path
                  d={s.d}
                  fill={s.color}
                  stroke="#fff"
                  strokeWidth="2"
                  onClick={() => setActiveBucket(s.key)}
                  className="cursor-pointer transition-opacity"
                  style={{ opacity: activeBucket && activeBucket !== s.key ? 0.4 : 1 }}
                >
                  <title>{s.label}: {s.count} district{s.count === 1 ? '' : 's'}</title>
                </path>
                {s.frac >= 0.05 && (
                  <text x={s.lx} y={s.ly} textAnchor="middle" dominantBaseline="central" fontSize="14" fontWeight="700" fill="#fff" pointerEvents="none">
                    {s.count}
                  </text>
                )}
              </g>
            ))}
            <text x={CX} y={CY - 6} textAnchor="middle" fontSize="11" fill="#64748b">Total</text>
            <text x={CX} y={CY + 12} textAnchor="middle" fontSize="18" fontWeight="700" fill="#0f172a">{buckets.total}</text>
          </svg>
        </div>

        {/* Legend + interactive list */}
        <div className="space-y-2">
          {slices.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveBucket(s.key === activeBucket ? null : s.key)}
              className={`w-full text-left border p-2 flex items-center justify-between gap-3 transition-colors ${activeBucket === s.key ? 'bg-slate-100 border-slate-400' : 'border-slate-200 hover:bg-slate-50'}`}
            >
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: s.color }} />
                <span className="text-sm font-medium text-slate-800">{s.label}</span>
              </div>
              <div className="text-xs text-slate-600">
                <span className="font-bold text-slate-900">{s.count}</span>
                <span className="ml-1">district{s.count === 1 ? '' : 's'} ({buckets.total ? ((s.count / buckets.total) * 100).toFixed(0) : 0}%)</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {activeList && (
        <div className="border-t border-slate-200 px-4 py-3 bg-slate-50">
          <div className="flex items-center justify-between mb-2">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              <span className="inline-block w-2.5 h-2.5 rounded mr-1 align-middle" style={{ backgroundColor: activeSlice.color }} />
              {activeSlice.label} — {activeList.length} district{activeList.length === 1 ? '' : 's'}
            </div>
            <button onClick={() => setActiveBucket(null)} className="text-[11px] text-rose-700 hover:text-rose-900">Clear</button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-1.5">
            {activeList.map(d => (
              <button
                key={d}
                onClick={() => onSelectDistrict && onSelectDistrict(d)}
                title={`Open ${d}`}
                className="text-xs px-2 py-1 bg-white border border-slate-200 hover:border-slate-400 hover:bg-slate-100 text-slate-800 truncate text-left"
              >{d}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
